import { useActionState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { sendTransactionalEmail } from "@/lib/sendTransactionalEmail";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, CheckCircle2 } from "lucide-react";

import { subscriberSchema } from "@/lib/schema";
import { checkRateLimit } from "@/lib/rateLimit";

interface NewsletterSignupProps {
  source?: string;
  variant?: "card" | "inline";
}

export function NewsletterSignup({
  source = "homepage",
  variant = "card",
}: NewsletterSignupProps) {
  const [state, formAction, isPending] = useActionState<
    { error: string | null; success: boolean },
    FormData
  >(
    async (_prevState, formData) => {
      if (!checkRateLimit("newsletter_signup", 3, 60000)) {
        return {
          error: "Too many attempts. Please try again later.",
          success: false,
        };
      }

      const parsed = subscriberSchema.safeParse({
        email: formData.get("email")?.toString().trim().toLowerCase(),
        source,
      });
      if (!parsed.success) {
        return { error: parsed.error.issues[0].message, success: false };
      }
      const { email } = parsed.data;

      const { error: dbError } = await supabase
        .from("newsletter_subscribers")
        .insert({ email, source });

      if (dbError) {
        if (dbError.code === "23505") {
          return { error: "You're already subscribed!", success: false };
        }
        return { error: dbError.message, success: false };
      }

      // Welcome email
      void sendTransactionalEmail({
        templateName: "newsletter-welcome",
        recipientEmail: email,
        idempotencyKey: `newsletter-welcome-${email}`,
        templateData: { source },
      });

      return { error: null, success: true };
    },
    { error: null, success: false },
  );

  if (state.success) {
    return (
      <div
        className={`${variant === "card" ? "rounded-lg border bg-card p-6" : ""} text-center`}
      >
        <CheckCircle2 className="h-10 w-10 text-primary mx-auto mb-2" />
        <h3 className="text-base font-bold text-foreground mb-1">You're in!</h3>
        <p className="text-sm text-muted-foreground">
          Check your inbox for the latest stories.
        </p>
      </div>
    );
  }

  if (variant === "inline") {
    return (
      <div className="w-full">
        <form action={formAction} className="flex flex-col gap-2">
          <div className="relative">
            <Input
              name="email"
              type="email"
              required
              placeholder="Enter your email"
              className="w-full h-10 rounded-xl bg-background/90 border-border/80 text-xs px-3.5 focus:border-primary/50"
            />
          </div>
          <Button
            type="submit"
            disabled={isPending}
            className="w-full h-9 rounded-xl text-xs font-semibold shadow-sm transition-all active:scale-[0.98]"
          >
            {isPending ? "Subscribing…" : "Get Weekly Briefing"}
          </Button>
        </form>
        {state.error && (
          <p className="text-xs text-destructive mt-1.5">{state.error}</p>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-card border border-border/80 p-6 md:p-8 shadow-sm">
      <div className="flex items-center gap-2.5 mb-2">
        <div className="h-9 w-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <Mail className="h-4 w-4" />
        </div>
        <h3 className="text-base font-bold text-foreground">
          Subscribe to Bloxida
        </h3>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Get curated stories, expert insights, and trends delivered straight to your inbox.
      </p>
      <form action={formAction} className="flex flex-col sm:flex-row gap-2">
        <Input
          name="email"
          type="email"
          required
          placeholder="you@example.com"
          className="flex-1 h-11 rounded-xl bg-background"
        />
        <Button type="submit" disabled={isPending} className="h-11 rounded-xl px-6 font-semibold shadow-sm">
          {isPending ? "Subscribing…" : "Subscribe"}
        </Button>
      </form>
      {state.error && (
        <p className="text-xs text-destructive mt-2">{state.error}</p>
      )}
    </div>
  );
}
